import { api } from '@/shared/services/api';

export interface SubscriptionStatus {
  subscription: {
    id: string;
    plan: string;
    planName: string;
    addons: string[];
    status: string;
    startedAt: string;
    expiresAt: string | null;
    trialEndsAt: string | null;
    daysUntilExpire: number | null;
    daysUntilTrialEnd: number | null;
    promoBonusMonths: number;
    paymentProvider?: string;
    cardMask?: string | null;
    cancelAtPeriodEnd?: boolean;
    entitled?: boolean;
  };
  usage: {
    period: string;
    invoicesUsed: number;
    invoicesLimit: number | null;
    invoicesRemaining: number | null;
    expensesUsed: number;
    expensesLimit: number | null;
    expensesRemaining: number | null;
    proformaUsed: number;
    proformaLimit: number | null;
    proformaRemaining: number | null;
    avizUsed: number;
    avizLimit: number | null;
    avizRemaining: number | null;
  };
  modules: {
    active: string[];
    available: string[];
  };
}

export interface LimitCheck {
  allowed: boolean;
  used: number;
  limit: number | null;
  remaining: number | null;
}

export interface ModuleDefinition {
  code: string;
  name: string;
  description: string;
  priceRonYear: number;
  priceRonMonthDisplay: number;
  features: string[];
}

export interface PlanDefinition {
  code: string;
  name: string;
  description: string;
  monthlyInvoiceLimit: number | null;
  monthlyExpenseLimit: number | null;
  durationMonths: number | null;
  priceRonYear: number;
  priceRonMonthDisplay: number;
}

export interface BundleDefinition {
  code: string;
  name: string;
  description: string;
  modules: string[];
  discountPercent: number;
  totalPriceRonYear: number;
  totalPriceRonMonthDisplay: number;
}

export interface Catalog {
  plans: PlanDefinition[];
  modules: ModuleDefinition[];
  bundles: BundleDefinition[];
}

export const billingApi = {
  getStatus: () => api.get<SubscriptionStatus>('/subscriptions/status'),
  getCatalog: () => api.get<Catalog>('/subscriptions/catalog'),
  canIssueInvoice: () => api.get<LimitCheck>('/subscriptions/can-issue-invoice'),
  canRecordExpense: () => api.get<LimitCheck>('/subscriptions/can-record-expense'),
  activateModule: (moduleCode: string) => api.post<any>(`/subscriptions/activate-module/${moduleCode}`),
  // Oferta «firma noua»: eligibilitate on-demand (face lookup ANAF pe server) + claim
  getNewCompanyOffer: () => api.get<any>('/subscriptions/new-company-offer'),
  claimNewCompanyOffer: () => api.post<any>('/subscriptions/claim-new-company-offer'),
  deactivateModule: (moduleCode: string) => api.post<any>(`/subscriptions/deactivate-module/${moduleCode}`),
  createCheckout: (params: { modules?: string[]; bundleCode?: string }) =>
    api.post<{ sessionId: string; url: string }>('/subscriptions/checkout', params),
  createProformaBankTransfer: (modules: string[]) => api.post<{ proformaId: string; series: string; number: number; dueDate: string; total: number; iban: string }>('/subscriptions/proforma-bank-transfer', { modules }),
  createPortal: () => api.post<{ url: string }>('/subscriptions/portal', {}),
  setupCard: () => api.post<{ sessionId: string; url: string }>('/subscriptions/setup-card', {}),
  listInvoices: () => api.get<{ invoices: StripeInvoice[] }>('/subscriptions/invoices'),
  getPaymentMethod: () => api.get<PaymentMethodInfo>('/subscriptions/payment-method'),
  setAutoRenew: (enabled: boolean) => api.post<{ ok: boolean; autoRenew: boolean }>('/subscriptions/auto-renew', { enabled }),
  createNetopiaV2Checkout: (params: { modules?: string[]; bundleCode?: string }) =>
    api.post<NetopiaV2CheckoutResponse>('/subscriptions/netopia-v2/checkout', params),
  createNetopiaCheckout: (params: { modules?: string[]; bundleCode?: string }) =>
    api.post<NetopiaCheckoutResponse>('/subscriptions/netopia/checkout', params),
  getNetopiaTransactionStatus: (orderId: string) =>
    api.get<NetopiaTransactionStatus>(`/subscriptions/netopia/transaction/${orderId}/status`),
};

export interface StripeInvoice {
  id: string;
  number: string | null;
  date: string | null;
  amount: number;
  currency: string;
  status: string | null;
  pdfUrl: string | null;
  hostedUrl: string | null;
  description: string | null;
  periodStart: string | null;
  periodEnd: string | null;
}

export interface PaymentMethodInfo {
  hasPaymentMethod: boolean;
  type?: string;
  brand?: string;
  last4?: string;
  expMonth?: number;
  expYear?: number;
}

export interface NetopiaCheckoutResponse {
  url: string;
  envKey: string;
  data: string;
  iv: string;
  cipher: string;
  orderId: string;
}

export interface NetopiaTransactionStatus {
  orderId: string;
  status: string; // pending | confirmed | paid | canceled | failed | unknown
  amount?: string;
  currency?: string;
  errorMessage?: string | null;
}

export interface NetopiaV2CheckoutResponse {
  paymentURL: string;
  orderID: string;
  ntpID?: string;
}
